import { formatDateTime } from 'shared/utils/time/format-date-time.ts';
import { TRIP_TASK_STATUS_MAP, type ETripTaskStatus } from 'types/trip-task-status';
import { ETripTaskType, TripTaskType } from 'types/trips';
import type { IAuditEntityReference, IAuditLogChangeGroup } from 'components/common/audit-logs/types.ts';
import { BaseAuditLogService } from './base-audit-log-service.ts';
import type { IAuditLogService, TAuditMessageSegment } from './types.ts';

export class TripTaskAuditLogService extends BaseAuditLogService implements IAuditLogService {
  public override get messageSegments(): TAuditMessageSegment[] {
    switch (this.auditLog.action) {
      case 'created':
        return this.createdMessageSegments;
      case 'updated':
        return this.updatedMessageSegments;
      case 'cargo_linked':
        return this.cargoLinkedMessageSegments;
      case 'cargo_unlinked':
        return this.cargoUnlinkedMessageSegments;
      case 'status_changed':
        return this.statusChangedMessageSegments;
      case 'reordered':
        return this.reorderedMessageSegments;
      default:
        return super.messageSegments;
    }
  }

  public override get changeGroups(): IAuditLogChangeGroup[] {
    switch (this.auditLog.action) {
      case 'cargo_linked':
      case 'cargo_unlinked':
        return this.buildCargoChangeGroups('Cargos after change');
      case 'created':
        return this.buildCargoChangeGroups('Cargos at creation');
      default:
        return super.changeGroups;
    }
  }

  protected override formatValue(field: string, value: unknown): string {
    if (typeof value !== 'string' || value === '') return super.formatValue(field, value);

    switch (field) {
      case 'status':
        return this.formatStatus(value);
      case 'fleethandStartedAt':
      case 'fleethandDoneAt':
        return formatDateTime(value);
      default:
        return super.formatValue(field, value);
    }
  }

  private get createdMessageSegments(): TAuditMessageSegment[] {
    const relatedTripId = this.auditLog.metadata?.relatedTripId as number | undefined;

    if (this.taskType === ETripTaskType.trailer_swap && relatedTripId) {
      const relatedTrip = this.buildEntityReference('trip', relatedTripId);

      return [
        this.text(`${this.actor} created ${this.taskLabel} to`),
        this.entity(relatedTrip),
        this.text(`(#${this.auditLog.entityId})`),
      ];
    }

    const action = this.isAutoCreated ? 'automatically created' : 'created';

    return [this.text(`${this.actor} ${action} ${this.taskLabel}`), this.text(`(#${this.auditLog.entityId})`)];
  }

  private get updatedMessageSegments(): TAuditMessageSegment[] {
    return [this.text(`${this.actor} updated ${this.taskLabel}`), this.text(`(#${this.auditLog.entityId})`)];
  }

  private get reorderedMessageSegments(): TAuditMessageSegment[] {
    return [this.text(`${this.actor} reordered ${this.taskLabel}`), this.text(`(#${this.auditLog.entityId})`)];
  }

  private get cargoLinkedMessageSegments(): TAuditMessageSegment[] {
    return [
      this.text(`${this.actor} added cargos`),
      ...this.buildCargoMessageSegments(this.addedCargoCompositeIds),
      this.text('to'),
      this.text(this.taskLabel),
      this.text(`(#${this.auditLog.entityId})`),
    ];
  }

  private get cargoUnlinkedMessageSegments(): TAuditMessageSegment[] {
    return [
      this.text(`${this.actor} removed cargos`),
      ...this.buildCargoMessageSegments(this.removedCargoCompositeIds),
      this.text('from'),
      this.text(this.taskLabel),
      this.text(`(#${this.auditLog.entityId})`),
    ];
  }

  private get statusChangedMessageSegments(): TAuditMessageSegment[] {
    const statusChange = this.getChange('status');

    const segments = [
      this.text(`${this.actor} changed the status of ${this.taskLabel}`),
      this.text(`(#${this.auditLog.entityId})`),
    ];

    if (!statusChange) return segments;

    return [
      ...segments,
      this.text('from'),
      this.text(this.formatStatus(statusChange.old)),
      this.text('to'),
      this.text(this.formatStatus(statusChange.new)),
    ];
  }

  private get taskType(): ETripTaskType {
    return this.auditLog.metadata?.taskType as ETripTaskType;
  }

  private get taskLabel(): string {
    return `task ${TripTaskType[this.taskType]}`;
  }

  private get isAutoCreated(): boolean {
    return this.auditLog.metadata?.autoCreated === true;
  }

  private get addedCargoCompositeIds(): string[] {
    return (this.auditLog.metadata?.addedCargoCompositeIds as string[] | undefined) ?? [];
  }

  private get removedCargoCompositeIds(): string[] {
    return (this.auditLog.metadata?.removedCargoCompositeIds as string[] | undefined) ?? [];
  }

  private get cargoCompositeIds(): string[] {
    return (this.auditLog.metadata?.cargoCompositeIds as string[] | undefined) ?? [];
  }

  private get cargoReferences(): IAuditEntityReference[] {
    return this.buildCargoReferences(this.cargoCompositeIds);
  }

  private buildCargoChangeGroups(fieldName: string): IAuditLogChangeGroup[] {
    return this.cargoCompositeIds.length
      ? [{ changes: [{ fieldName, references: this.cargoReferences, type: 'entities' }] }]
      : [];
  }

  private buildCargoReferences(compositeIds: string[]): IAuditEntityReference[] {
    return compositeIds.map((compositeId) => ({
      entityId: Number(compositeId.split('-')[1]),
      entityType: 'cargo',
      label: compositeId,
    }));
  }

  private buildCargoMessageSegments(compositeIds: string[]): TAuditMessageSegment[] {
    const references = this.buildCargoReferences(compositeIds);

    return references.map((reference, index) =>
      this.entity({
        ...reference,
        label: index < references.length - 1 ? `${reference.label},` : reference.label,
      }),
    );
  }

  private formatStatus(status: unknown): string {
    return TRIP_TASK_STATUS_MAP[status as ETripTaskStatus] ?? String(status);
  }
}
